import type { UseMobileAppReturn } from '../types'

import {
    computed,
    useCoreConfig,
    useDevice,
    useState,
    createSharedComposable
} from '#imports'

/**
 * @description Native (React Native WebView) app context: detection, version
 * gating, and the outbound message bridge. Intentionally free of any passkey or
 * webauthn knowledge so the many non-passkey consumers (gtag, playgame, footer,
 * notifications, ...) can depend on it without pulling in useWebauthn.
 */
export const useMobileApp = createSharedComposable((): UseMobileAppReturn => {
    const config = useCoreConfig()
    const { userAgent } = useDevice()

    const isMobileApp = import.meta.client ? 'ReactNativeWebView' in window : false

    const isMobileAppAndRequiresUpdate = computed(() => {
        // Only ever prompt inside the native app; guards against a non-app client
        // (or a spoofed UA containing PlategoApp/x.y.z) tripping the update dialog.
        if (!isMobileApp) return false

        const getAppVersion = () => {
            const match = userAgent.match(/(PlategoApp)\/([0-9.]+)/)
            return match ? match[2] : null
        }

        const getVersionParts = (v: string): { major: number, minor: number, patch: number } => {
            const parts = v.split('.')

            return {
                major: Number(parts[0]) || 0,
                minor: Number(parts[1]) || 0,
                patch: Number(parts[2]) || 0
            }
        }

        const version = getAppVersion() // 2.1.4

        if (!version) return

        const minVersionRequired = config.app?.minVersionRequired ?? '2.1.5' // App version that changes to semantic versioning

        // If the app still uses the old versioning
        if (!version.includes('.')) { // e.g. 7 - Old way of versioning
            if (minVersionRequired.includes('.')) return true // New semantic versioning - if the config has a semantic version set, then the user needs to update the app

            // Both use old versioning - compare as numbers
            // User needs update if their version is less than required
            return Number(version) < Number(minVersionRequired)
        }

        // App uses semantic versioning but web config hasn't been updated yet
        // Don't prompt for update in this case
        if (!minVersionRequired.includes('.')) return false

        // Both use semantic versioning - compare properly
        const appParts = getVersionParts(version)
        const requiredParts = getVersionParts(minVersionRequired)

        if (appParts.major < requiredParts.major) return true
        if (appParts.major > requiredParts.major) return false

        if (appParts.minor < requiredParts.minor) return true
        if (appParts.minor > requiredParts.minor) return false

        return appParts.patch < requiredParts.patch
    })

    /**
     * @description Session id of an eid flow the app reported as still waiting for its result
     * (Android MobilePay), cleared when that result arrives.
     */
    const pendingEidSessionId = useState<string | undefined>('mobile-app:pending-eid-session', () => undefined)

    function postToApp(message: Record<string, unknown>) {
        if (!import.meta.client) return
        window.ReactNativeWebView?.postMessage(JSON.stringify(message))
    }

    return {
        isMobileApp,
        isMobileAppAndRequiresUpdate,
        postToApp,
        pendingEidSessionId,
    }
})
